import { Checkbox, createStyles, UnstyledButton } from '@mantine/core';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo, useState } from 'react';

import Image from '~/domains/common/components/Image/Image';
import Modal from '~/domains/common/components/Modal/Modal/Modal';
import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import DrawerTitle from '~/domains/design-system/components/Drawer/DrawerTitle';

import { reportQuickPicksFilters } from '../../../analyticsEvents';
import { useBoardContext } from '../../../context/BoardContext/BoardContext';
import { useQuickPicksFilters } from '../../../hooks/useQuickPicksFilters';

const useStyles = createStyles((theme) => ({
  gamesWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._8,
    marginTop: theme.other.spacing._16,

    [theme.fn.smallerThan('sm')]: {
      flexGrow: 1,
      overflow: 'auto',
      padding: theme.other.spacing._16,
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  game: {
    backgroundColor: theme.colors.gray[1],
    borderRadius: theme.radius.md,
    padding: '10px 16px 10px 14px',
    fontSize: theme.fontSizes.md,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    cursor: 'pointer',
  },
  gameContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._8,
  },
  gameDetails: {
    color: theme.colors.gray[6],
  },
  label: {},
  drawerBody: {
    maxHeight: '100svh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  actionContainer: {
    paddingTop: theme.other.spacing._16,
    position: 'sticky',
    bottom: 0,

    [theme.fn.smallerThan('sm')]: {
      padding: theme.other.spacing._16,
      flexShrink: 0,
    },
  },
}));

function GameFilterModal({ isOpened, onClose }: { isOpened: boolean; onClose: () => void }) {
  const { t } = useTranslation('contest');
  const { gamesByLeague } = useQuickPicksFilters();
  const { filters } = useBoardContext();
  const { classes } = useStyles();
  const selectedLeague = filters.value.league;
  const selectedGames = filters.value.game ?? [];
  const [tempSelectedGames, setTempSelectedGames] = useState<string[]>(selectedGames);
  const [tempAllSelected, setTempAllSelected] = useState<boolean>(selectedGames.length === 0);

  const handleSubmit = useCallback(() => {
    filters.set('game', tempSelectedGames);
    reportQuickPicksFilters({
      league: filters.value.league,
      type: filters.value.type,
      game: tempSelectedGames,
    });
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.set, onClose, tempSelectedGames]);

  const handleAllChange = useCallback(() => {
    setTempAllSelected((prevState) => !prevState);
    setTempSelectedGames([]);
  }, []);

  const leagueGames = useMemo(
    () => gamesByLeague[selectedLeague] ?? [],
    [gamesByLeague, selectedLeague]
  );

  const handleGameChange = useCallback(
    (gameId: string) => {
      setTempSelectedGames((prevState) => {
        if (!prevState) {
          return [gameId];
        }

        if (tempAllSelected) {
          setTempAllSelected(false);
          return leagueGames.map((game) => game.id).filter((id) => id !== gameId);
        }

        if (prevState.includes(gameId)) {
          return prevState.filter((id) => id !== gameId);
        }

        if (prevState.length === leagueGames.length - 1) {
          setTempAllSelected(true);
        }

        return [...prevState, gameId];
      });
    },
    [leagueGames, tempAllSelected]
  );

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      classNames={{ drawer: { body: classes.drawerBody } }}
    >
      <DrawerTitle isAddTopPaddingOnMobile>
        {t('quickPick.board.filters.gameModal.title')}
      </DrawerTitle>
      <div className={classes.gamesWrapper}>
        <UnstyledButton className={classes.game} onClick={handleAllChange}>
          <div className={classes.gameContent}>
            <Checkbox checked={tempAllSelected} />
            <Text>{t('quickPick.board.filters.gameModal.allGames')}</Text>
          </div>
        </UnstyledButton>
        {leagueGames.map((game) => (
          <UnstyledButton
            className={classes.game}
            key={game.id}
            onClick={() => handleGameChange(game.id)}
          >
            <div className={classes.gameContent}>
              <Checkbox checked={tempSelectedGames.includes(game.id) || tempAllSelected} />
              <Image
                src={getTeamOrPlayerImageUrl(selectedLeague, game.away.id)}
                width={30}
                height={24}
                alt={game.away.alias}
                fallbackComponent={<div />}
              />
              <Text>
                {game.away.alias} @ {game.home.alias}
              </Text>
              <Image
                src={getTeamOrPlayerImageUrl(selectedLeague, game.home.id)}
                width={30}
                height={24}
                alt={game.home.alias}
                fallbackComponent={<div />}
              />
            </div>
            <div className={classes.gameDetails}>
              <Text>
                {format(game.start_date, 'EEE, LLL d')} • {format(game.start_date, 'h:mm a')}
              </Text>
            </div>
          </UnstyledButton>
        ))}
      </div>
      <div className={classes.actionContainer}>
        <Button variant="primary-fill" size="medium" fullWidth onClick={handleSubmit}>
          {t('quickPick.board.filters.gameModal.submit')}
        </Button>
      </div>
    </Modal>
  );
}

export { GameFilterModal };
